import { useEffect, useState } from 'react';
import { reference, type LocalNotice } from '../lib/localdb.js';
import { acknowledgeNotice } from '../lib/actions.js';
import { subscribe } from '../lib/outbox.js';
import { text } from '../lib/format.js';
import { BigButton } from '../components/Controls.js';

/**
 * Things this technician has to be told.
 *
 * Two shapes, and the difference is decided by whoever wrote the notice rather than by this
 * screen:
 *
 *   - **Blocking** — the terms of use, the Law 25 geolocation notice, a safety rule. The app
 *     does not open until it is accepted. It is a full screen because a wall of text behind a
 *     banner is a wall of text nobody reads, and the whole value of the record is that they
 *     did.
 *   - **Acknowledge** or **informational** — a recall, a rule, a recurring issue. A banner at
 *     the top of the day, tapped away, never in the way of work.
 *
 * Everything here renders from the local database, so a notice published while a technician
 * was underground still greets them when they surface — and can be accepted there, because
 * the acknowledgement queues like any other write. Locking somebody out of their morning for
 * want of a radio would be the wrong failure.
 */

export async function loadNotices(): Promise<LocalNotice[]> {
  return reference<LocalNotice[]>('notices', []);
}

export const outstandingBlocking = (notices: LocalNotice[]): LocalNotice[] =>
  notices.filter((notice) => notice.enforcement === 'blocking' && !notice.acknowledgedAt);

export const outstandingBanners = (notices: LocalNotice[]): LocalNotice[] =>
  notices.filter((notice) => notice.enforcement !== 'blocking' && !notice.acknowledgedAt);

/**
 * The gate.
 *
 * One notice at a time, in the order the server put them in — blocking first, newest first —
 * because three stacked walls of text is how somebody learns to scroll to the bottom without
 * reading. Accepting one reveals the next.
 */
export function NoticeGate({ notices, onDone }: { notices: LocalNotice[]; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [readToEnd, setReadToEnd] = useState(false);
  const notice = notices[0];

  useEffect(() => {
    // A new notice starts unread, however far the previous one was scrolled.
    setReadToEnd(false);
  }, [notice?.versionId]);

  if (!notice) return null;

  const accept = async () => {
    setBusy(true);
    try {
      await acknowledgeNotice(notice.versionId);
      if (notices.length === 1) onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gate">
      <header className="gate__head">
        <h1>{text(notice.title)}</h1>
        <p className="quiet">
          Version {notice.version}
          {notices.length > 1 ? ` · ${notices.length} avis à lire` : ''}
        </p>
      </header>

      {/*
        Scrolled to the end before the button works. Not a dark pattern in reverse: the point
        of the record is that somebody read it, and a button that is live before the text has
        moved makes the acknowledgement worth nothing.
      */}
      <div
        className="gate__body"
        onScroll={(event) => {
          const el = event.currentTarget;
          if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setReadToEnd(true);
        }}
        ref={(el) => {
          // A short notice that does not scroll is read by definition.
          if (el && el.scrollHeight <= el.clientHeight + 8) setReadToEnd(true);
        }}
      >
        <p>{text(notice.body)}</p>
        {notice.publicUrl && (
          <p className="quiet">
            <a href={notice.publicUrl} target="_blank" rel="noreferrer">
              Lire en ligne
            </a>
          </p>
        )}
      </div>

      <footer className="gate__act">
        <BigButton
          onClick={() => void accept()}
          busy={busy}
          disabled={!readToEnd}
          hint={readToEnd ? 'Votre acceptation est enregistrée avec la date' : 'Faites défiler jusqu’à la fin'}
        >
          J’ai lu et j’accepte
        </BigButton>
      </footer>
    </div>
  );
}

/** A notice that does not stop anybody: read it, tap it away, get on with the day. */
export function NoticeBanner({ notice, onAcknowledged }: { notice: LocalNotice; onAcknowledged: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const tone =
    notice.kind === 'recall' || notice.kind === 'safety' ? 'warn' : notice.kind === 'rule' ? 'rule' : 'note';

  return (
    <div className={`notice notice--${tone}`} role="status">
      <button type="button" className="notice__head" onClick={() => setOpen((value) => !value)}>
        <span className="notice__title">{text(notice.title)}</span>
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="notice__body">
          <p>{text(notice.body)}</p>
          {notice.publicUrl && (
            <a href={notice.publicUrl} target="_blank" rel="noreferrer">
              Lire en ligne
            </a>
          )}
        </div>
      )}
      {/* `informational` is read, not agreed to. Offering a button would overstate the record. */}
      {notice.enforcement === 'acknowledge' && (
        <button
          type="button"
          className="notice__ack"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await acknowledgeNotice(notice.versionId);
              onAcknowledged();
            } finally {
              setBusy(false);
            }
          }}
        >
          Compris
        </button>
      )}
    </div>
  );
}

/** Re-render the banners whenever the queue moves, so an acknowledgement clears at once. */
export function useNotices(): [LocalNotice[], () => void] {
  const [notices, setNotices] = useState<LocalNotice[]>([]);

  const reload = () => void loadNotices().then(setNotices);

  useEffect(() => {
    reload();
    return subscribe(reload);
  }, []);

  return [notices, reload];
}
